import { useId } from 'react';
import IconCaretUp from '@/assets/svg/icon-caret-up';
import { Text } from '@/components/animated-type/text';
import { styled } from '@/styles/stitches.config';
import { Trans } from '@lingui/macro';
import delay from 'delay';
import { MouseEvent, useState } from 'react';
import { visuallyHidden } from '../../styles/predefined';

export type Answer = {
  id: string;
  label: string;
};

type StoryQuestionProps<T extends Answer> = {
  answers: T[];
  answerHeading?: string;
  selectedAnswer?: T;
  question: string;
  response?: string;
  onAnswer: (answer: T) => void;
};

const Root = styled('section', {
  minHeight: '100vh',
  color: 'var(--color-text-light)',
});

const Form = styled('form', {
  marginBlockStart: '$8',
});

const Fieldset = styled('fieldset', {
  border: 0,
  paddingBlock: 0,
  paddingInline: '$3',
});

const Legend = styled('legend', {
  fontSize: '$4',
  fontWeight: '$semibold',
  lineHeight: '$2',
  margin: 0,
  marginBlockEnd: '$8',

  '[lang=ta] &': {
    fontSize: '$3',
    lineHeight: '$3',
  },

  '@bp6': {
    maxWidth: '45rem',
  },

  '@bp11': {
    marginInline: 'auto',
    textAlign: 'center',
  },
});

const AnswerHeading = styled('p', {
  maxWidth: '40ch',
  marginBlockStart: 0,
  marginBlockEnd: '$5',
  marginInline: 'auto',

  textTransform: 'uppercase',
  textAlign: 'center',
  fontWeight: '$semibold',
  letterSpacing: '0.05em',
  lineHeight: '$2',

  '[dir=rtl] &': {
    letterSpacing: 0,
  },
});

const SelectedAnswerRoot = styled('div', {
  display: 'grid',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  overflow: 'hidden',
});

const SelectedAnswer = styled('p', {
  fontSize: 'clamp($4, 5vw, $7)',
  fontWeight: '$bold',
  textAlign: 'center',
  maxWidth: '25ch',
  marginBlockEnd: 0,
});

const SelectedAnswerPlaceholder = styled('div', {
  display: 'grid',
  alignItems: 'center',
  backgroundColor: '$blue0',
  color: '$white',
  maxWidth: '40ch',
  paddingBlock: '$4',
  paddingInline: '$5',
  borderRadius: '$3',
  marginBlock: 0,
  marginInline: '$4',

  '@bp4': {
    margin: 0,
    marginInline: 'auto',
  },
});

const ReturnToQuestionLink = styled('a', {
  alignItems: 'center',
  color: 'inherit',
  border: '1px solid currentColor',
  borderRadius: '$1',
  paddingBlock: '$2',
  paddingInline: '$4',
  marginBlockStart: '$3',
  marginBlockEnd: '$4',
  marginInline: 'auto',
  textDecoration: 'none',

  '&:hover': {
    color: '$blue0',
    backgroundColor: '$white',
  },

  '&:active': {
    color: '$blue0',
    backgroundColor: '$white',
  },

  '&:focus-visible, &.focus-visible': {
    color: '$blue0',
    outlineColor: '$white',
    outlineWidth: 3,
    outlineStyle: 'dotted',
    backgroundColor: '$white',
  },
});

const ReturnToQuestionIcon = styled(IconCaretUp, {
  marginInlineEnd: '$2',
});

const Centered = styled('p', {
  alignSelf: 'center',
});

export function useStoryQuestion<T extends Answer>() {
  const [selectedAnswer, setSelectedAnswer] = useState<T>();

  return {
    selectedAnswer,
    handleAnswer: (answer: T) => setSelectedAnswer(answer),
  };
}

export function StoryQuestion<T extends Answer>(props: StoryQuestionProps<T>) {
  const { question, answers, answerHeading, selectedAnswer, response, onAnswer } = props;

  const name = useId();
  const selectedAnswerRootId = useId();
  const formId = useId();
  const [triggerAnswerAnimation, setTriggerAnswerAnimation] = useState(Date.now());

  const handleAnswerClick = async (answer: T) => {
    onAnswer(answer);

    const el = document?.querySelector(`#answer-${CSS.escape(selectedAnswerRootId)}`);
    if (el) {
      // force animation
      setTriggerAnswerAnimation(Date.now());

      // wait for 200ms after answering to give the user
      // some time to parse that answering has succeeded
      // this makes the scroll animation less jarring
      await delay(200);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleReturnToFormClick = async (evt: MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault(); // block the native scroll to id.
    const form = document?.querySelector(`#form-${CSS.escape(formId)}`);
    if (form) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Root>
      <Form onSubmit={(evt) => evt.preventDefault()} id={'form-' + formId}>
        <Fieldset>
          <Legend>{question}</Legend>
          {answerHeading && <AnswerHeading>{answerHeading}</AnswerHeading>}
          {answers.map((answer) => (
            <Answer
              key={answer.id}
              name={name}
              answer={answer}
              onClick={handleAnswerClick}
              checked={answer.id === selectedAnswer?.id}
            />
          ))}
        </Fieldset>
      </Form>

      <SelectedAnswerRoot id={`answer-${selectedAnswerRootId}`}>
        {selectedAnswer && response && (
          <SelectedAnswer aria-live="polite">
            <Text triggerAnimation={triggerAnswerAnimation} type="answer">
              <span>{response}</span>
            </Text>
          </SelectedAnswer>
        )}

        {!selectedAnswer && (
          <SelectedAnswerPlaceholder>
            <Centered>
              <Trans id="story-question.placeholder.text">
                Answer the question above to learn more about the changing nature of childhood.
              </Trans>
            </Centered>

            <ReturnToQuestionLink onClick={handleReturnToFormClick} href={`#${formId}`}>
              <ReturnToQuestionIcon />
              <Trans id="story-question.placeholder.button">Return to the question</Trans>
            </ReturnToQuestionLink>
          </SelectedAnswerPlaceholder>
        )}
      </SelectedAnswerRoot>
    </Root>
  );
}

type AnswerProps<T> = {
  name?: string;
  answer: T;
  checked: boolean;
  onClick: (answer: T) => void;
};

const AnswerRoot = styled('div', {
  display: 'grid',
  justifyContent: 'center',
  marginBlockEnd: '$3',
});

const Label = styled('label', {
  display: 'inline-block',
  paddingBlock: '$2',
  paddingInline: '$5',
  textAlign: 'center',
  lineHeight: '$3',
  borderRadius: '$4',
  boxShadow: '0 0 $space$2 0 $colors$shadows',
  backgroundColor: '$blue0',
  maxWidth: '40ch',
  border: '3px solid $blue0',
  color: '$white',

  'input:hover + &': {
    borderColor: '$white',
    cursor: 'pointer',
  },

  'input:focus-visible + &': {
    border: '3px dotted $white',
  },

  'input.focus-visible + &': {
    border: '3px dotted $white',
  },

  'input:checked + &': {
    backgroundColor: 'var(--color-primary)',
    borderColor: 'var(--color-primary)',
  },
});

const HiddenInput = styled('input', visuallyHidden);

function Answer<T extends Answer>(props: AnswerProps<T>) {
  const { name, answer, onClick } = props;

  const id = useId();

  return (
    <AnswerRoot>
      <HiddenInput
        type="radio"
        id={id}
        name={name}
        value={answer.id}
        onClick={() => onClick(answer)}
        autoComplete="off"
      />
      <Label htmlFor={id}>{answer.label}</Label>
    </AnswerRoot>
  );
}
